import { Link } from 'react-router-dom';
import { codeLabel } from '../../utils/codeLabel';
import { useTranslation } from 'react-i18next';
import { OrderLabel } from './OrderLabel';
import { StatusChip } from './StatusChip';
import { useMemo, useState } from 'react';
import type {
  PaymentDirection,
  PaymentStatus,
  PaymentTransactionFilters,
  PaymentTransactionRow,
} from '../../api/types';
import { formatMoney, parsePaise } from './allocation';
import { addCalendarDays, formatIstDate, istDateInput } from './paymentDates';
import styles from './Payments.module.css';
import { DatePicker } from '../../components/ui/DatePicker';
import { Button } from '../../components/ui/Button';

function rangeDays(from: string, to: string) {
  if (!from || !to) return 0;
  return (
    Math.floor((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
  );
}

export function TransactionsTab({
  rows,
  totalElements,
  page,
  today,
  counterparties = [],
  onFiltersChange,
  onOpenPayment,
}: {
  rows: PaymentTransactionRow[];
  totalElements: number;
  page: number;
  today?: string;
  counterparties?: Array<{ id: number; label: string }>;
  onFiltersChange: (filters: PaymentTransactionFilters & { size: 50 }) => void;
  onOpenPayment: (paymentId: number) => void;
}) {
  const { t } = useTranslation();
  const currentDay = today ?? istDateInput();
  const [filters, setFilters] = useState<PaymentTransactionFilters>({
    from: addCalendarDays(currentDay, -29),
    to: currentDay,
    page,
  });
  const error = useMemo(
    () =>
      !filters.from || !filters.to
        ? 'From and to dates are required.'
        : rangeDays(filters.from, filters.to) > 92
          ? 'Choose a date range of 92 days or less.'
          : rangeDays(filters.from, filters.to) < 1
            ? 'The from date must be before the to date.'
            : null,
    [filters.from, filters.to],
  );
  const change = (next: Partial<PaymentTransactionFilters>) => {
    const updated = { ...filters, ...next };
    setFilters(updated);
    const nextError =
      !updated.from ||
      !updated.to ||
      rangeDays(updated.from, updated.to) > 92 ||
      rangeDays(updated.from, updated.to) < 1;
    if (!nextError) onFiltersChange({ ...updated, page: updated.page ?? 0, size: 50 });
  };
  const pages = Math.ceil(totalElements / 50);
  return (
    <section>
      <div className={styles.card}>
        <div className={styles.filters}>
          <label className={styles.field}>
            {t('payments.transactionsTab.from')}
            <DatePicker
              aria-label={t('payments.transactionsTab.fromDate')}
              required
              value={filters.from}
              onChange={(from) => change({ from, page: 0 })}
            />
          </label>
          <label className={styles.field}>
            {t('payments.transactionsTab.to')}
            <DatePicker
              aria-label={t('payments.transactionsTab.toDate')}
              required
              value={filters.to}
              onChange={(to) => change({ to, page: 0 })}
            />
          </label>
          <label className={styles.field}>
            {t('payments.transactionsTab.direction')}
            <select
              aria-label={t('payments.transactionsTab.direction')}
              value={filters.direction ?? ''}
              onChange={(event) =>
                change({
                  direction: (event.target.value || undefined) as PaymentDirection | undefined,
                  page: 0,
                })
              }
            >
              <option value="">{t('payments.transactionsTab.allMovements')}</option>
              <option value="IN">{t('payments.transactionsTab.inShippers')}</option>
              <option value="OUT">{t('payments.transactionsTab.outPartners')}</option>
            </select>
          </label>
          <label className={styles.field}>
            {t('payments.transactionsTab.status')}
            <select
              aria-label={t('payments.transactionsTab.status')}
              value={filters.status ?? ''}
              onChange={(event) =>
                change({
                  status: (event.target.value || undefined) as PaymentStatus | undefined,
                  page: 0,
                })
              }
            >
              <option value="">{t('payments.transactionsTab.allStatuses')}</option>
              {['PAID', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'VOID'].map((status) => (
                <option key={status} value={status}>
                  {codeLabel('paymentStatus', status)}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            {t('payments.transactionsTab.counterparty')}
            <select
              aria-label={t('payments.transactionsTab.counterparty')}
              value={filters.counterpartyId ?? ''}
              onChange={(event) =>
                change({
                  counterpartyId: event.target.value ? Number(event.target.value) : undefined,
                  page: 0,
                })
              }
            >
              <option value="">{t('payments.transactionsTab.allCounterparties')}</option>
              {counterparties.map((counterparty) => (
                <option key={counterparty.id} value={counterparty.id}>
                  {counterparty.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error && (
          <div role="alert" className={styles.error}>
            {error}
          </div>
        )}
      </div>
      <div className={styles.card}>
        <div className={styles.sectionTitle}>
          <div>
            <h3>{t('payments.transactionsTab.heading')}</h3>
            <p>
              {t('payments.transactionsTab.transactionCount', { total: totalElements })} · <span>{t('payments.transactionsTab.perPage')}</span>
            </p>
          </div>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('payments.transactionsTab.colPayment')}</th>
                <th>{t('payments.transactionsTab.colDirection')}</th>
                <th>{t('payments.transactionsTab.colCounterparty')}</th>
                <th>{t('payments.transactionsTab.colOrders')}</th>
                <th>{t('payments.transactionsTab.colMethod')}</th>
                <th>{t('payments.transactionsTab.colAmount')}</th>
                <th>{t('payments.transactionsTab.colDate')}</th>
                <th>{t('payments.transactionsTab.colStatus')}</th>
                <th>{t('payments.transactionsTab.colAction')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className={styles.mono}>{row.reference ?? `#${row.id}`}</span>
                    <button className={styles.orderLink} onClick={() => onOpenPayment(row.id)}>
                      #{row.id}
                    </button>
                  </td>
                  <td>{row.direction}</td>
                  <td>
                    <Link className={styles.orderLink} to={`/users/${row.counterpartyId}`}>
                      {row.counterpartyName ?? 'Counterparty'}
                    </Link>
                  </td>
                  <td>
                    {row.orderIds?.map((id) => (
                      <div key={id}>
                        <OrderLabel shipmentId={id} />
                      </div>
                    ))}
                  </td>
                  <td>{row.method ?? '—'}</td>
                  <td className={styles.mono}>{formatMoney(parsePaise(row.amount))}</td>
                  <td>{formatIstDate(row.paidAt ?? row.createdAt)}</td>
                  <td>
                    <StatusChip displayStatus={row.status} />
                  </td>
                  <td>
                    <Button variant="secondary" size="sm" onClick={() => onOpenPayment(row.id)}>
                      {t('payments.transactionsTab.viewDetails')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.pagination}>
          <span>
            {t('payments.transactionsTab.pageOf', { page: page + 1, pages: Math.max(1, pages) })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 0}
            onClick={() => change({ page: page - 1 })}
          >
            {t('payments.transactionsTab.previousPage')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('payments.transactionsTab.nextPage')}
            disabled={page + 1 >= pages}
            onClick={() => change({ page: page + 1 })}
          >
            {t('payments.transactionsTab.nextPage')}
          </Button>
        </div>
      </div>
    </section>
  );
}
